import React from "react";
import { cx } from "utils/join-classnames";
import usePermissions from "ui/hooks/usePermissions";
import {
    Button,
    Table,
    AddonModal,
    LoaderContainer,
    ConfirmActionModal,
} from "ui/components";
import { useSessionContext } from "ui/context/SessionContext";
import { MediaFile } from "core/models";
import { listMediaFiles, deleteMediaFile } from "core/services/site/mediafile";
import styles from "./Addons.module.scss";

type SortByType = "name" | "type" | "aspect-ratio";

const SettingsPage: React.FC = () => {
    const userPermissions = usePermissions();
    const [addmodal, setAddmodal] = React.useState<boolean>(false);
    const [editmodal, setEditmodal] = React.useState<boolean>(false);
    const [deleteModal, setDeleteModal] = React.useState<boolean>(false);
    const [deleteAddonId, setDeleteAddonId] = React.useState<number>();
    const [deletePending, setDeletePending] = React.useState<boolean>(false);
    const [editableAddon, setEditableAddon] = React.useState<MediaFile>();
    const sessionContext = useSessionContext();

    const userCanManageAddons = userPermissions.canManageAddons;

    const useAddons = () => {
        const [addons, setAddons] = React.useState<MediaFile[]>([]);
        const [pending, setPending] = React.useState<boolean>(false);
        const getAddons = async () => {
            setPending(true);
            try {
                if (!sessionContext.session) {
                    throw Error("missing session");
                }
                if (!sessionContext.selectedProject) {
                    throw Error("missing project");
                }
                const result = await listMediaFiles(
                    sessionContext.session.token,
                    sessionContext.selectedProject.id,
                );
                if (result.error || !result.value) {
                    throw new Error("not able to fetch addons");
                }
                setPending(false);
                return result.value.filter(
                    (mediafile) =>
                        mediafile.type === "Intro" ||
                        mediafile.type === "Outro" ||
                        mediafile.type === "Overlay",
                );
            } catch (err) {
                setPending(false);
                return undefined;
            }
        };

        const refreshAddons = React.useCallback(async () => {
            if (!userCanManageAddons) {
                return;
            }
            const fetchedAddons = await getAddons();
            if (fetchedAddons) {
                setAddons(fetchedAddons);
            }
        }, []);

        const deleteAddon = async () => {
            try {
                if (!sessionContext.session) {
                    throw Error("missing session");
                }
                setDeletePending(true);
                if (!deleteAddonId) {
                    throw new Error("no AddonID was found");
                }
                const result = await deleteMediaFile(
                    sessionContext.session.token,
                    deleteAddonId,
                );
                if (result.error || !result.value) {
                    throw new Error(
                        "somehting went wrong deleting the MediaFile",
                    );
                }
                refreshAddons();
                setDeleteModal(false);
                setDeletePending(false);
            } catch (err) {
                setDeleteModal(false);
                setDeletePending(false);
            }
        };
        React.useEffect(() => {
            refreshAddons();
        }, [refreshAddons]);

        return {
            addons,
            pending,
            deleteAddon,
            refreshAddons,
        };
    };

    function filterAddons(
        addons: MediaFile[],
        filterType: keyof MediaFile["type"] | "all",
        filterAspectRatioLabel: string,
        filterName: string,
    ) {
        const lowerCased = filterName.toLowerCase();
        return addons.filter((addon) => {
            if (
                filterAspectRatioLabel &&
                filterAspectRatioLabel !==
                    addon.mediaFileProperties.mediaProperties.videoAspectRatio
            ) {
                return false;
            }
            if (filterType !== "all" && !addon.type[filterType]) {
                return false;
            }
            if (!addon.name.toLowerCase().includes(lowerCased)) {
                return false;
            }

            return true;
        });
    }

    const useTable = () => {
        const [sortBy, setSortBy] = React.useState<SortByType>("name");
        const [sortOrder, setSortOrder] =
            React.useState<Table.SortOrderType>("ascending");
        const sortByFactory = React.useCallback((sortByValue: SortByType) => {
            setSortBy((prev) => {
                if (prev === sortByValue) {
                    setSortOrder((prevSortOrder) =>
                        prevSortOrder === "ascending"
                            ? "descending"
                            : "ascending",
                    );
                } else {
                    setSortOrder("ascending");
                }

                return sortByValue;
            });
        }, []);
        const sortByName = React.useCallback(() => {
            sortByFactory("name");
        }, [sortByFactory]);
        const sortByValue = React.useCallback(
            () => sortByFactory("type"),
            [sortByFactory],
        );
        const sortByAspectRatio = React.useCallback(
            () => sortByFactory("aspect-ratio"),
            [sortByFactory],
        );

        return {
            sortBy,
            sortByName,
            sortByValue,
            sortByAspectRatio,
            sortOrder,
        };
    };

    const { sortBy, sortOrder, sortByName, sortByValue, sortByAspectRatio } =
        useTable();
    const [filterRatioLabel] = React.useState<string>("");
    const [filterType] = React.useState<keyof MediaFile["type"] | "all">("all");
    const [filterName] = React.useState("");
    const { addons, pending, refreshAddons, deleteAddon } = useAddons();

    const filteredAddons = React.useMemo(
        () => filterAddons(addons, filterType, filterRatioLabel, filterName),
        [addons, filterType, filterRatioLabel, filterName],
    );

    const sortedAddons = React.useMemo(() => {
        if (sortBy && filteredAddons) {
            const sorted = [...filteredAddons].sort((a, b) => {
                if (sortBy === "aspect-ratio") {
                    return a.getAspectRatio() < b.getAspectRatio() ? -1 : 1;
                }
                if (sortBy === "name") {
                    return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
                }
                return JSON.stringify(a.type) < JSON.stringify(b.type) ? -1 : 1;
            });

            if (sortOrder === "descending") {
                sorted.reverse();
            }

            return sorted;
        }
        return filteredAddons;
    }, [filteredAddons, sortBy, sortOrder]);

    const handleCloseModal = () => {
        setAddmodal(false);
        setEditmodal(false);
    };
    const handleSaveModalAdd = () => {
        refreshAddons();
        setAddmodal(false);
    };
    const handleSaveModalEdit = async () => {
        refreshAddons();
        setEditmodal(false);
    };
    const handleAddBtn = () => {
        setAddmodal(true);
    };
    const handleEditAddon = (addon: MediaFile) => {
        setEditableAddon(addon);
        setEditmodal(true);
    };
    const handleDeleteAddon = (addonId: number) => {
        setDeleteAddonId(addonId);
        setDeleteModal(true);
    };
    const closeDeleteModal = () => {
        setDeleteModal(false);
        setDeleteAddonId(undefined);
    };
    const handleConformDeleteAddon = () => {
        deleteAddon();
    };
    if (!userCanManageAddons) {
        return (
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Not available
                    </h1>
                    <span className={styles.heading__desc}>
                        You don&apos;t seem to have permission
                    </span>
                </div>
            </div>
        );
    }

    if (!userPermissions.hasFeatureEditFragment) {
        return (
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Not available
                    </h1>
                    <span className={styles.heading__desc}>
                        Upgrade your plan to manage intros, outros, and overlays
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.section}>
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Addons
                    </h1>
                    <span className={styles.heading__desc}>
                        Add intros, outros and overlays to your project. Every
                        aspect ratio you want to use within SpotOn will require
                        its own special addons. For example if you want to add
                        an intro to a 1:1 (square) video you&#39;ll have to
                        upload a square addon here.
                        <br />
                        <br />
                        It is recommended to use the highest resolution you
                        need, as SpotOn will automatically scale an addon to fit
                        the content.
                    </span>
                </div>
                <div className={styles.heading__right}>
                    <Button icon="plus" type="primary" onClick={handleAddBtn}>
                        Add addon
                    </Button>
                </div>
            </div>

            {pending && (
                <LoaderContainer message="Loading addons…" theme="dark" />
            )}
            {!pending && addons.length === 0 && (
                <div className={styles.emptymsg}>There are no addons yet!</div>
            )}
            {addons.length > 0 && (
                <Table.Container>
                    <Table.Head sortable>
                        <Table.Label
                            sortable
                            sortActive={sortBy === "name"}
                            sortOrder={
                                sortBy === "name" ? sortOrder : undefined
                            }
                            label="Name"
                            onClick={sortByName}
                            width="filled"
                        />
                        <Table.Label
                            sortable
                            sortActive={sortBy === "type"}
                            sortOrder={
                                sortBy === "type" ? sortOrder : undefined
                            }
                            label="Type"
                            onClick={sortByValue}
                            width="filled"
                        />
                        <Table.Label
                            sortable
                            sortActive={sortBy === "aspect-ratio"}
                            sortOrder={
                                sortBy === "aspect-ratio"
                                    ? sortOrder
                                    : undefined
                            }
                            label="Aspect ratios"
                            onClick={sortByAspectRatio}
                            width="filled"
                        />
                        <Table.Label label="" width="filled" />
                    </Table.Head>
                    <Table.Body>
                        {sortedAddons.map((addon) => (
                            <Table.Row key={addon.id}>
                                <Table.Cell width="filled">
                                    {addon.name}
                                </Table.Cell>
                                <Table.Cell width="filled">
                                    {addon.type}
                                </Table.Cell>
                                <Table.Cell width="filled">{`${addon.getAspectRatio()}`}</Table.Cell>
                                <Table.Cell width="filled">
                                    <Button
                                        type="icon"
                                        icon="edit"
                                        theme="dark"
                                        className={styles.actionBtn}
                                        onClick={() => handleEditAddon(addon)}
                                    />
                                    <Button
                                        type="icon"
                                        icon="delete"
                                        theme="dark"
                                        className={styles.actionBtn}
                                        onClick={() =>
                                            handleDeleteAddon(addon.id)
                                        }
                                    />
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Container>
            )}

            {addmodal && (
                <AddonModal
                    type="add"
                    onClose={handleCloseModal}
                    onSave={handleSaveModalAdd}
                />
            )}
            {editmodal && (
                <AddonModal
                    type="edit"
                    onClose={handleCloseModal}
                    onSave={handleSaveModalEdit}
                    editableAddon={editableAddon}
                />
            )}
            {deleteModal && (
                <ConfirmActionModal
                    title="Delete addon"
                    description="are you sure you wish to delete this addon?"
                    handleCancel={closeDeleteModal}
                    theme="light"
                    confirmBtnText={
                        deletePending ? "Deleting addon..." : "Delete"
                    }
                    handleConfirm={handleConformDeleteAddon}
                    pending={deletePending}
                />
            )}
        </div>
    );
};

export default SettingsPage;
